import { createContext, createElement, type ReactNode, useContext } from 'react';
import { noopLogger, type TelemetryLogger } from '../types.js';

export type { ErrorContext, TelemetryLogger } from '../types.js';

/**
 * Hands the app's error reporter to components, so shared component libraries can report errors without knowing
 * whether the app sends them to OpenTelemetry, Sentry or both. Without a provider, reports go nowhere.
 *
 * Kept on its own entry point (`@trinity/observability-web/react`) so importing it does not load the OpenTelemetry SDK.
 * Written without JSX so consumers with a different JSX runtime (e.g. emotion's) can compile it unchanged.
 */
export const TelemetryLoggerContext = createContext<TelemetryLogger>(noopLogger);

export function TelemetryLoggerProvider({
  logger,
  children,
}: {
  logger: TelemetryLogger;
  children: ReactNode;
}) {
  return createElement(TelemetryLoggerContext.Provider, { value: logger }, children);
}

export function useTelemetryLogger(): TelemetryLogger {
  return useContext(TelemetryLoggerContext);
}
